import { type ComponentType, lazy } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { CaixaOnlyPdvRouteGuard } from '@/components/auth/CaixaOnlyPdvRouteGuard';
import { SuperadminRoute } from '@/components/auth/SuperadminRoute';
import { AuthenticatedLayout } from '@/components/layout/AuthenticatedLayout';
import { SuperadminLayout } from '@/components/superadmin/SuperadminLayout';
import { LoginPage } from '@/pages/Login';
import { PortalClientePage } from '@/pages/portal/PortalClientePage';
import { RedefinirSenhaInicialPage } from '@/pages/auth/RedefinirSenhaInicialPage';
// Import estático deliberado (não `React.lazy`): a rota pública `/` não tem shell
// com <Suspense> por trás (o fallback vive em AppShell.tsx/SuperadminLayout.tsx),
// e um chunk lazy sobre o HTML pré-renderizado geraria tela em branco antes do
// JS carregar — exceção já prevista no CLAUDE.md §2.2, mesma razão de LoginPage.
// NotFoundPage segue a mesma regra: catch-all público, sem shell.
import { LandingPage } from '@/pages/site/LandingPage';
import { NotFoundPage } from '@/pages/site/NotFoundPage';

/**
 * Todas as páginas atrás de login usam `React.lazy` (code-splitting por rota) —
 * regra obrigatória, ver docs-arquitetura/13-diretrives-visuais-ux.md.md §10.2.
 * As páginas exportam named exports (não default), daí o `.then(pick(...))`.
 * O fallback de <Suspense> (PageSkeleton) fica no shell — AppShell.tsx e
 * SuperadminLayout.tsx — para a sidebar/header não desmontarem a cada troca de rota.
 * Login/Portal/Redefinir-senha ficam eager: são a primeira tela, sem shell por trás
 * para segurar um skeleton, e o lazy ali só adicionaria uma requisição extra.
 */
function pick<T extends Record<string, ComponentType<unknown>>, K extends keyof T>(key: K) {
  return (mod: T) => ({ default: mod[key] });
}

const DashboardPage = lazy(() => import('@/pages/Dashboard').then(pick('DashboardPage')));
const EstoquePage = lazy(() => import('@/pages/Estoque').then(pick('EstoquePage')));
const ClientesPage = lazy(() => import('@/pages/clientes/ClientesPage').then(pick('ClientesPage')));
const VeiculosPage = lazy(() => import('@/pages/veiculos/VeiculosPage').then(pick('VeiculosPage')));
const ProdutosPage = lazy(() => import('@/pages/produtos/ProdutosPage').then(pick('ProdutosPage')));
const OsPage = lazy(() => import('@/pages/os/OsPage').then(pick('OsPage')));
const OsDetalhePage = lazy(() => import('@/pages/os/OsDetalhePage').then(pick('OsDetalhePage')));
const OsListagemPage = lazy(() => import('@/pages/os/OsListagemPage').then(pick('OsListagemPage')));
const OrcamentosPage = lazy(() => import('@/pages/os/OrcamentosPage').then(pick('OrcamentosPage')));
const PdvPage = lazy(() => import('@/pages/pdv/PdvPage').then(pick('PdvPage')));
const PdvVendasPage = lazy(() => import('@/pages/pdv/PdvVendasPage').then(pick('PdvVendasPage')));
const RelatoriosPage = lazy(() => import('@/pages/relatorios/RelatoriosPage').then(pick('RelatoriosPage')));
const RelatorioOsPage = lazy(() => import('@/pages/relatorios/RelatorioOsPage').then(pick('RelatorioOsPage')));
const ConfiguracoesPage = lazy(() => import('@/pages/configuracoes/ConfiguracoesPage').then(pick('ConfiguracoesPage')));
const AuditoriaPage = lazy(() => import('@/pages/auditoria/AuditoriaPage').then(pick('AuditoriaPage')));
const RetornosPage = lazy(() => import('@/pages/retornos/RetornosPage').then(pick('RetornosPage')));
const CampanhasPage = lazy(() => import('@/pages/campanhas/CampanhasPage').then(pick('CampanhasPage')));
const CamposCustomizadosPage = lazy(() =>
  import('@/pages/campos-customizados/CamposCustomizadosPage').then(pick('CamposCustomizadosPage')),
);
const FornecedoresPage = lazy(() => import('@/pages/fornecedores/FornecedoresPage').then(pick('FornecedoresPage')));
const FinanceiroPage = lazy(() => import('@/pages/financeiro/FinanceiroPage').then(pick('FinanceiroPage')));
const PacoteContadorPage = lazy(() => import('@/pages/contador/PacoteContadorPage').then(pick('PacoteContadorPage')));
const TransferenciasPage = lazy(() =>
  import('@/pages/transferencias/TransferenciasPage').then(pick('TransferenciasPage')),
);
const CaixaPage = lazy(() => import('@/pages/caixa/CaixaPage').then(pick('CaixaPage')));
const UsuariosPage = lazy(() => import('@/pages/usuarios/UsuariosPage').then(pick('UsuariosPage')));
const EntradasCompraPage = lazy(() =>
  import('@/pages/entradas-compra/EntradasCompraPage').then(pick('EntradasCompraPage')),
);
const NotificacoesPage = lazy(() => import('@/pages/notificacoes/NotificacoesPage').then(pick('NotificacoesPage')));
const AgendamentosPage = lazy(() => import('@/pages/agendamentos/AgendamentosPage').then(pick('AgendamentosPage')));
const InventarioPage = lazy(() => import('@/pages/inventario/InventarioPage').then(pick('InventarioPage')));
const MecanicoOsPage = lazy(() => import('@/pages/mecanico/MecanicoOsPage').then(pick('MecanicoOsPage')));
const FidelidadePage = lazy(() => import('@/pages/fidelidade/FidelidadePage').then(pick('FidelidadePage')));
const SuperadminPainelPage = lazy(() =>
  import('@/pages/superadmin/SuperadminPainelPage').then(pick('SuperadminPainelPage')),
);
const OrganizacoesPage = lazy(() => import('@/pages/superadmin/OrganizacoesPage').then(pick('OrganizacoesPage')));
const OrganizacaoDetalhePage = lazy(() =>
  import('@/pages/superadmin/OrganizacaoDetalhePage').then(pick('OrganizacaoDetalhePage')),
);
const AdminsPage = lazy(() => import('@/pages/superadmin/AdminsPage').then(pick('AdminsPage')));
const AssistentePage = lazy(() => import('@/pages/assistente/AssistentePage').then(pick('AssistentePage')));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      retry: 1,
    },
  },
});

/**
 * Rota `/` decide entre a Landing pública e o redirect para `/painel` — a
 * decisão de auth fica aqui, nunca dentro de `LandingPage` (que é renderizada
 * isolada, sem AuthProvider, pelo build de prerender).
 */
function PublicHome() {
  const { isAuthenticated } = useAuth();
  if (isAuthenticated) {
    return <Navigate to="/painel" replace />;
  }
  return <LandingPage />;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route index element={<PublicHome />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/portal" element={<PortalClientePage />} />
            <Route path="/redefinir-senha" element={<RedefinirSenhaInicialPage />} />

            {/* Painel SUPERADMIN */}
            <Route element={<SuperadminRoute />}>
              <Route element={<SuperadminLayout />}>
                <Route path="superadmin" element={<SuperadminPainelPage />} />
                <Route path="superadmin/organizacoes" element={<OrganizacoesPage />} />
                <Route path="superadmin/organizacoes/:id" element={<OrganizacaoDetalhePage />} />
                <Route path="superadmin/admins" element={<AdminsPage />} />
              </Route>
            </Route>

            <Route element={<ProtectedRoute />}>
              <Route element={<CaixaOnlyPdvRouteGuard />}>
                <Route element={<AuthenticatedLayout />}>
                  <Route path="painel" element={<DashboardPage />} />
                  <Route path="clientes" element={<ClientesPage />} />
                  <Route path="veiculos" element={<VeiculosPage />} />
                  <Route path="produtos" element={<ProdutosPage />} />
                  <Route path="estoque" element={<EstoquePage />} />
                  <Route path="entradas-compra" element={<EntradasCompraPage />} />
                  <Route path="os/listagem" element={<OsListagemPage />} />
                  <Route path="os/orcamentos" element={<OrcamentosPage />} />
                  <Route path="os/:id" element={<OsDetalhePage />} />
                  <Route path="os" element={<OsPage />} />
                  <Route path="pdv/balcao" element={<Navigate to="/pdv" replace />} />
                  <Route path="pdv/vendas" element={<PdvVendasPage />} />
                  <Route path="pdv" element={<PdvPage />} />
                  <Route path="relatorios" element={<RelatoriosPage />} />
                  <Route path="relatorios/os" element={<RelatorioOsPage />} />
                  <Route path="fornecedores" element={<FornecedoresPage />} />
                  <Route path="financeiro" element={<FinanceiroPage />} />
                  <Route path="contador" element={<PacoteContadorPage />} />
                  <Route path="transferencias" element={<TransferenciasPage />} />
                  <Route path="caixa" element={<CaixaPage />} />
                  <Route path="usuarios" element={<UsuariosPage />} />
                  <Route path="notificacoes" element={<NotificacoesPage />} />
                  <Route path="agendamentos" element={<AgendamentosPage />} />
                  <Route path="retornos" element={<RetornosPage />} />
                  <Route path="campanhas" element={<CampanhasPage />} />
                  <Route path="inventario" element={<InventarioPage />} />
                  <Route path="configuracoes" element={<ConfiguracoesPage />} />
                  <Route path="auditoria" element={<AuditoriaPage />} />
                  <Route path="campos-customizados" element={<CamposCustomizadosPage />} />
                  <Route path="fidelidade" element={<FidelidadePage />} />
                  <Route path="assistente" element={<AssistentePage />} />
                  <Route path="mecanico/os" element={<MecanicoOsPage />} />
                </Route>
              </Route>
            </Route>
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </BrowserRouter>
        <Toaster
          position="top-right"
          richColors
          closeButton
          duration={4000}
          offset="1rem"
          gap={10}
          visibleToasts={4}
          toastOptions={{
            duration: 4000,
            classNames: { title: 'font-sans text-sm', description: 'font-sans text-sm' },
          }}
        />
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
